import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { listAppUsers, userDisplay, type AppUser } from "../../services/userService";
import { apiUrl } from "../../utils/api";
import type { Gespraechspartner } from "../../services/contactNoteService";
import { alleDokumente, alleFenster, elementMitId } from "../windowManager/browserFenster/fensterRegister";

/**
 * Gesprächspartner einer Tagebuch-Notiz — EIN Feld für interne Mitarbeiter und
 * externe Kontakte.
 *
 * Bewusst ein kombinierter Picker statt zweier getrennter Felder: beim
 * Protokollieren eines Gesprächs denkt niemand in „intern/extern", sondern
 * „mit wem habe ich gesprochen". Die Herkunft wird als Badge an der Zeile und
 * am Chip sichtbar gemacht.
 *
 * Suchquellen: `listAppUsers()` (gecacht, clientseitig gefiltert) und
 * `GET /contact?search=` (debounced, Muster wie LinkRow). Vorgeschlagen werden
 * ohne Suchbegriff die verbundenen Kontakte des aktuellen Kunden — das sind in
 * der Praxis die Ansprechpartner, mit denen gesprochen wird.
 */

export interface KontaktRef {
    id: number;
    label: string;
}

interface Props {
    value: Gespraechspartner[];
    onChange: (next: Gespraechspartner[]) => void;
    /** Kontakt, dessen Tagebuch offen ist — speist die Vorschlagsliste. */
    contactId: number;
    /** Vorab bekannte Labels externer Kontakte (aus bereits geladenen Notizen). */
    knownContacts?: KontaktRef[];
}

const useStyles = makeStyles({
    wrap: { position: "relative" },
    field: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...shorthands.gap("6px"),
        minHeight: "38px",
        ...shorthands.padding("6px", "8px"),
        ...shorthands.border("1px", "solid", "#ece7f0"),
        borderRadius: "10px",
        backgroundColor: "#fbf9fc",
        cursor: "text",
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("2px", "8px"),
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: "#efe3f2",
        color: "#4d2051",
        ...shorthands.border("1px", "solid", "#d9c4de"),
    },
    chipExtern: {
        backgroundColor: "#eef0f4",
        color: "#4a5568",
        ...shorthands.border("1px", "solid", "#d7dce4"),
    },
    chipX: {
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        color: "inherit",
        cursor: "pointer",
        fontSize: "13px",
        lineHeight: "1",
        ...shorthands.padding("0"),
    },
    input: {
        flex: 1,
        minWidth: "120px",
        ...shorthands.border("0"),
        outlineStyle: "none",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        fontSize: "13px",
    },
    row: {
        display: "flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("7px", "12px"),
        cursor: "pointer",
        fontSize: "13px",
        ":hover": { backgroundColor: "#faf6fb" },
    },
    badge: {
        flexShrink: 0,
        ...shorthands.padding("1px", "6px"),
        borderRadius: "999px",
        fontSize: "10px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
    },
    badgeIntern: { backgroundColor: "#efe3f2", color: "#4d2051" },
    badgeExtern: { backgroundColor: "#eef0f4", color: "#4a5568" },
    hint: {
        ...shorthands.padding("8px", "12px"),
        fontSize: "12px",
        color: "#6f6c75",
        fontStyle: "italic",
    },
});

type Suggestion =
    | { kind: "intern"; userId: string; label: string; sub?: string }
    | { kind: "extern"; contactId: number; label: string; sub?: string };

/** Anzeigename eines Kontakts aus den üblichen Feldern. */
function contactLabel(c: {
    displayName?: string | null;
    companyName?: string | null;
    lastname?: string | null;
    firstName?: string | null;
    id: number;
}): string {
    return (
        c.displayName ||
        [c.lastname, c.firstName].filter(Boolean).join(", ") ||
        c.companyName ||
        `Kontakt #${c.id}`
    );
}

export const GespraechspartnerPicker: React.FC<Props> = ({
    value,
    onChange,
    contactId,
    knownContacts,
}) => {
    const styles = useStyles();
    const [users, setUsers] = useState<AppUser[]>([]);
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [contacts, setContacts] = useState<KontaktRef[]>([]);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const fieldRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Labels bereits gewählter/bekannter externer Kontakte, damit Chips nicht
    // als „Kontakt #42" stehen bleiben, wenn die Suche sie nicht (mehr) liefert.
    const [labelCache, setLabelCache] = useState<Map<number, string>>(new Map());
    useEffect(() => {
        if (!knownContacts?.length) return;
        setLabelCache((prev) => {
            const next = new Map(prev);
            for (const k of knownContacts) next.set(k.id, k.label);
            return next;
        });
    }, [knownContacts]);

    useEffect(() => {
        listAppUsers()
            .then(setUsers)
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 250);
        return () => clearTimeout(t);
    }, [query]);

    // Kontaktsuche: mit Suchbegriff global, ohne Suchbegriff die verbundenen
    // Kontakte des aktuellen Kunden (= die üblichen Ansprechpartner).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const run = async () => {
            try {
                const q = debounced.trim();
                const url = q
                    ? apiUrl("contact", { search: q, limit: 20 })
                    : apiUrl(`contact-relation/all-related/${contactId}`);
                const res = await fetch(url);
                if (!res.ok) {
                    if (!cancelled && !q) setContacts([]);
                    return;
                }
                const json = await res.json();
                const rows: any[] = Array.isArray(json) ? json : (json?.data ?? json?.items ?? []);
                if (cancelled) return;
                const mapped = rows
                    .map((r) => {
                        const c = r?.contact ?? r?.target ?? r;
                        const id = Number(c?.id);
                        if (!Number.isFinite(id) || id <= 0) return null;
                        return { id, label: contactLabel({ ...c, id }) };
                    })
                    .filter(Boolean) as KontaktRef[];
                setContacts(mapped);
                setLabelCache((prev) => {
                    const next = new Map(prev);
                    for (const c of mapped) next.set(c.id, c.label);
                    return next;
                });
            } catch {
                if (!cancelled) setContacts([]);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [debounced, open, contactId]);

    const chosenIntern = useMemo(
        () => new Set(value.filter((v) => v.kind === "intern").map((v) => (v as any).userId)),
        [value],
    );
    const chosenExtern = useMemo(
        () => new Set(value.filter((v) => v.kind === "extern").map((v) => (v as any).contactId)),
        [value],
    );

    const suggestions = useMemo<Suggestion[]>(() => {
        const q = debounced.trim().toLowerCase();
        const internHits: Suggestion[] = users
            .filter((u) => !chosenIntern.has(u.id))
            .filter(
                (u) =>
                    !q ||
                    userDisplay(u).toLowerCase().includes(q) ||
                    (u.email ?? "").toLowerCase().includes(q) ||
                    (u.username ?? "").toLowerCase().includes(q),
            )
            .slice(0, 8)
            .map((u) => ({
                kind: "intern" as const,
                userId: u.id,
                label: userDisplay(u),
                sub: u.email ?? undefined,
            }));
        const externHits: Suggestion[] = contacts
            .filter((c) => !chosenExtern.has(c.id))
            .slice(0, 12)
            .map((c) => ({ kind: "extern" as const, contactId: c.id, label: c.label }));
        return [...internHits, ...externHits];
    }, [users, contacts, debounced, chosenIntern, chosenExtern]);

    // Dropdown per Portal (fixed) — entkommt overflow-Containern des Dialogs.
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            const r = fieldRef.current?.getBoundingClientRect();
            if (r) setPos({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        reposition();
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
        };
    }, [open]);

    // Klick außerhalb schließt.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (fieldRef.current?.contains(e.target as Node)) return;
            const drop = elementMitId("gp-picker-drop");
            if (drop?.contains(e.target as Node)) return;
            setOpen(false);
        };
        alleDokumente.addEventListener("pointerdown", onDown);
        return () => alleDokumente.removeEventListener("pointerdown", onDown);
    }, [open]);

    const add = (s: Suggestion) => {
        const next: Gespraechspartner =
            s.kind === "intern"
                ? { kind: "intern", userId: s.userId }
                : { kind: "extern", contactId: s.contactId };
        onChange([...value, next]);
        setQuery("");
        inputRef.current?.focus();
    };

    const removeAt = (idx: number) => {
        onChange(value.filter((_, i) => i !== idx));
    };

    const labelOf = (gp: Gespraechspartner): string => {
        if (gp.kind === "intern") {
            const u = users.find((x) => x.id === gp.userId);
            return u ? userDisplay(u) : gp.userId;
        }
        // Frisch Gesuchtes aus dem Cache, sonst das vom Backend gelieferte
        // Label der bereits gespeicherten Notiz.
        return labelCache.get(gp.contactId) ?? gp.label ?? "Externer Kontakt";
    };

    return (
        <div className={styles.wrap}>
            <div
                ref={fieldRef}
                className={styles.field}
                onClick={() => {
                    setOpen(true);
                    inputRef.current?.focus();
                }}
            >
                {value.map((gp, idx) => (
                    <span
                        key={`${gp.kind}-${gp.kind === "intern" ? gp.userId : gp.contactId}`}
                        className={mergeClasses(styles.chip, (gp.kind === "extern") && styles.chipExtern)}
                    >
                        {labelOf(gp)}
                        <button
                            type="button"
                            className={styles.chipX}
                            aria-label={`${labelOf(gp)} entfernen`}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeAt(idx);
                            }}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    className={styles.input}
                    value={query}
                    placeholder={value.length ? "" : "Mitarbeiter oder Kontakt suchen…"}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") setOpen(false);
                        if (e.key === "Backspace" && !query && value.length) {
                            removeAt(value.length - 1);
                        }
                    }}
                />
            </div>

            {open &&
                pos &&
                createPortal(
                    <div
                        id="gp-picker-drop"
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width: pos.width,
                            zIndex: 100000,
                            backgroundColor: "#fff",
                            border: "1px solid #ece7f0",
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(20,8,20,0.12)",
                            maxHeight: 300,
                            overflowY: "auto",
                        }}
                    >
                        {suggestions.length === 0 ? (
                            <div className={styles.hint}>
                                {debounced.trim() ? "Keine Treffer." : "Tippen, um zu suchen."}
                            </div>
                        ) : (
                            suggestions.map((s) => (
                                <div
                                    key={s.kind === "intern" ? `i${s.userId}` : `e${s.contactId}`}
                                    className={styles.row}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        add(s);
                                    }}
                                >
                                    <span
                                        className={mergeClasses(styles.badge, s.kind === "intern" ? styles.badgeIntern : styles.badgeExtern)}
                                    >
                                        {s.kind === "intern" ? "intern" : "extern"}
                                    </span>
                                    <span>{s.label}</span>
                                    {s.sub && (
                                        <span style={{ color: "#6b5a66", fontSize: 11 }}>{s.sub}</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
